/**
 * useUnreadCounts Hook
 * 
 * Hook để quản lý unread counts cho tất cả conversations
 * - Init: Gọi API để lấy danh sách unreadCounts
 * - new_message event: Tăng unread count nếu message được gửi cho user và chưa đọc
 * - message_read event: Giảm unread count tương ứng
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import websocketService from 'services/websocketService';
import counselorServices from 'services/counselorServices';
import { getAccessToken } from 'utils/sessionStorage';

interface UnreadCounts {
  [conversationId: string]: number;
}

interface UseUnreadCountsOptions {
  userId: number;
  enabled?: boolean;
  fromIds?: number[];
}

interface UseUnreadCountsReturn {
  unreadCounts: UnreadCounts;
  getUnreadCount: (conversationId: string) => number;
  decreaseUnreadCount: (conversationId: string, amount?: number) => void;
  fetchAllUnreadMessages: (fromIds?: number[]) => Promise<void>;
}

/**
 * Hook to manage unread counts for conversations
 */
export function useUnreadCounts(
  options: UseUnreadCountsOptions
): UseUnreadCountsReturn {
  const { userId, enabled = true, fromIds } = options;
  const [unreadCounts, setUnreadCounts] = useState<UnreadCounts>({});
  const isInitializedRef = useRef(false);

  // Get unread count for a specific conversation
  const getUnreadCount = useCallback((conversationId: string): number => {
    return unreadCounts[conversationId] || 0;
  }, [unreadCounts]);

  // Decrease unread count for a conversation (when marking messages as read)
  const decreaseUnreadCount = useCallback((conversationId: string, amount: number = 1) => {
    setUnreadCounts(prev => {
      const currentCount = prev[conversationId] || 0;
      const newCount = Math.max(0, currentCount - amount);
      
      if (newCount === 0) {
        // Remove from state if count is 0
        const { [conversationId]: _, ...rest } = prev;
        return rest;
      }
      
      return {
        ...prev,
        [conversationId]: newCount,
      };
    });
  }, []);

  // Fetch all unread messages from API (used on initialization)
  const fetchAllUnreadMessages = useCallback(async (filterFromIds?: number[]) => {
    if (!enabled || !userId) {
      return;
    }

    // Check if token exists before making API call
    const token = getAccessToken();
    if (!token) {
      // Don't make API call if there's no token (user not authenticated)
      return;
    }

    try {
      // Use filterFromIds if provided, otherwise use fromIds from options
      const idsToFilter = filterFromIds || fromIds;
      const response = await counselorServices.getUnreadMessages(userId, idsToFilter);
      
      if (response?.data && Array.isArray(response.data)) {
        const newUnreadCounts: UnreadCounts = {};
        
        (response.data as Array<{ conversation_id: string; unread_count: number }>).forEach((item) => {
          if (item.conversation_id && item.unread_count > 0) {
            newUnreadCounts[item.conversation_id] = item.unread_count;
          }
        });

        
        setUnreadCounts(newUnreadCounts);
      }
    } catch (error) {
      console.error('Error fetching all unread messages:', error);
    }
  }, [enabled, userId, fromIds]);

  // Initialize: Fetch unread counts on mount
  useEffect(() => {
    if (enabled && userId && !isInitializedRef.current) {
      fetchAllUnreadMessages();
      isInitializedRef.current = true;
    }
  }, [enabled, userId, fetchAllUnreadMessages]);

  // Setup WebSocket listeners for new_message and message_read events
  useEffect(() => {
    if (!enabled || !userId) {
      return;
    }

    let isMounted = true;

    // Handle new_message event: Increase unread count if message is for current user and not read
    const handleNewMessage = (message: any) => {
      if (!isMounted) return;
      
      // Check if message is sent to current user and not already read
      if (
        message.conversation_id &&
        Number(message.to_id) === userId &&
        message.status !== 'read' &&
        Number(message.from_id) !== userId // Don't count own messages
      ) {
        setUnreadCounts(prev => ({
          ...prev,
          [message.conversation_id]: (prev[message.conversation_id] || 0) + 1,
        }));
      }
    };

    // Handle message_read event: Decrease unread count
    const handleMessageRead = (data: any) => {
      if (!isMounted) return;
      
      if (data.conversation_id && data.user_id === userId) {
        setUnreadCounts(prev => {
          const currentCount = prev[data.conversation_id] || 0;
          if (currentCount > 0) {
            const newCount = currentCount - 1;
            
            if (newCount === 0) {
              // Remove from state if count is 0
              const { [data.conversation_id]: _, ...rest } = prev;
              return rest;
            }
            
            return {
              ...prev,
              [data.conversation_id]: newCount,
            };
          }
          return prev;
        });
      }
    };

    // Function to register listeners (will be called when socket is ready)
    const registerListeners = () => {
      const socket = websocketService.getSocket();
      if (socket && isMounted) {
        // Remove any existing listeners first to avoid duplicates
        socket.off('chat:new_message', handleNewMessage);
        socket.off('chat:message_read', handleMessageRead);
        
        // Register listeners
        socket.on('chat:new_message', handleNewMessage);
        socket.on('chat:message_read', handleMessageRead);
      }
    };

    // Handler for connect/reconnect events
    const onConnect = () => {
      if (isMounted) {
        registerListeners();
      }
    };

    // Try to connect and register listeners
    const initializeListeners = async () => {
      try {
        // Ensure socket is connected
        if (!websocketService.isSocketConnected()) {
          await websocketService.connect();
        }
        
        // Register listeners after connection is established
        registerListeners();
        
        // Also listen for connect/reconnect events to re-register listeners
        const socket = websocketService.getSocket();
        if (socket && isMounted) {
          socket.on('connect', onConnect);
          socket.on('reconnect', onConnect);
        }
      } catch (error) {
        console.error('Error initializing WebSocket listeners for unread counts:', error);
      }
    };

    // Initialize listeners
    initializeListeners();

    // Cleanup on unmount
    return () => {
      isMounted = false;
      
      const socket = websocketService.getSocket();
      if (socket) {
        socket.off('chat:new_message', handleNewMessage);
        socket.off('chat:message_read', handleMessageRead);
        socket.off('connect', onConnect);
        socket.off('reconnect', onConnect);
      }
    };
  }, [enabled, userId]);

  return {
    unreadCounts,
    getUnreadCount,
    decreaseUnreadCount,
    fetchAllUnreadMessages,
  };
}
